import {api, config, logger, notifier} from '@bitstillery/common/app'
import {ProductPhotoType} from '@bitstillery/common/api/fact2server'
import {cached} from '@bitstillery/common/lib/api_cache'
import {present} from '@bitstillery/common/lib/utils'
import {as_state} from '@bitstillery/common/lib/unchecked'

import type {CountriesOfOriginResponse, ProductPhotosResponse} from '@bitstillery/common/api/fact2server'

import type {StateArray} from '@bitstillery/mithril'

import {$s} from '@/app'

/** A photo as the portal keeps it: locations made absolute, and one photo per artkey marked primary. */
export interface ProductPhoto extends ProductPhotosResponse {
    image_location: string
    primary?: boolean
    thumbnail_location?: string
}

/**
 * Photos for a set of offer-item artkeys — public per artkey, so cached process-wide (keyed by the
 * artkey set) and carried across the SSR boundary. Keeps the spotlight/carousel from re-POSTing the
 * same photo lookup on every SSR request and again on the client's first render.
 */
const fetch_product_photos = cached(
    'portal/offer-items/product-photos',
    async (artkeys: number[]): Promise<ProductPhotosResponse[] | null> => {
        const {result, success} = await api.post('portal/offer-items/product-photos', {offer_item_artkeys: artkeys}, true)
        if (!success) {
            notifier.notify('Cannot load the product photos', 'warning')
            return null
        }
        return result
    },
    {
        ttl: 5 * 60_000,
        scope: 'public',
        key: (artkeys: number[]) => [...artkeys].toSorted((a, b) => a - b).join(','),
        // A failed lookup must not stick for the TTL; the next load retries it.
        store_if: (photos) => photos !== null,
    },
)

export {DISCOUNT_TYPES, offer_item_price} from '@/lib/price'

export async function load_country_of_origin(offer_item_artkeys: number[]) {
    const artkeys_to_fetch = offer_item_artkeys.filter((artkey) => !(artkey in $s.countries_of_origin))
    if (artkeys_to_fetch.length === 0) {
        return
    }
    logger.debug(`[offer] loading countries of origin for ${artkeys_to_fetch.length} products`)

    const {result, success} = await api.post(
        'portal/offer-items/countries-of-origin',
        {
            offer_item_artkeys: artkeys_to_fetch,
        },
        true,
    )
    if (!success) {
        notifier.notify('Cannot load the countries of origin', 'warning')
        return
    }

    // Keyed by offer item artkey; an artkey not seen before has no entry yet.
    const countries_of_origin: Record<string, CountriesOfOriginResponse[] | undefined> = $s.countries_of_origin
    for (const country of result) {
        const countries = countries_of_origin[country.artkey]
        if (countries) {
            countries.push(country)
        } else {
            $s.countries_of_origin[country.artkey] = as_state([country])
        }
    }
}

export async function load_product_photos(offer_item_artkeys: number[]) {
    const artkeys_to_fetch = offer_item_artkeys.filter((artkey) => !(artkey in $s.product_photos))
    if (artkeys_to_fetch.length === 0) {
        return
    }

    const result = await fetch_product_photos(artkeys_to_fetch)
    // Without an answer the artkeys stay unmarked, so a later call asks for them again.
    if (!result) return

    // Not all artkeys_to_fetch may return photos from the api. Diff the missing artkeys
    // From the api and add an empty placeholder, so it is not queried again.
    const artkey_matches = new Set(result.map((i) => i.artkey).filter((i) => artkeys_to_fetch.includes(i)))
    const missing_artkeys = artkeys_to_fetch.filter((i) => !artkey_matches.has(i))
    const product_photos: Record<number, ProductPhoto[]> = {}
    for (const missing_artkey of missing_artkeys) {
        if (!product_photos[missing_artkey]) {
            product_photos[missing_artkey] = []
        }
    }

    for (const photo of result) {
        let photos = product_photos[photo.artkey]
        if (!photos) {
            photos = []
            product_photos[photo.artkey] = photos
        }
        // The photo's storage location is a nullable column; without one there is no image to show.
        if (!photo.image_location) {
            continue
        }

        photos.push({
            artkey: photo.artkey,
            image_location: `${config.product_photo_host}/${photo.image_location}`,
            photo_type: photo.photo_type,
            rank: photo.rank,
            ...(photo.thumbnail_location ? {thumbnail_location: `${config.product_photo_host}/${photo.thumbnail_location}`} : {}),
        })
    }

    // Use an emptying pool per photos.length so placeholders don't repeat until all have been used once.
    const placeholder_pools: Record<number, number[] | undefined> = {}
    for (const photos of Object.values(product_photos)) {
        if (photos.length === 0) {
            continue
        }
        const use_placeholders = photos.some((i) => i.photo_type === ProductPhotoType.PLACEHOLDER)
        if (use_placeholders) {
            const len = photos.length
            let pool = placeholder_pools[len]
            if (!pool || pool.length === 0) {
                pool = Array.from({length: len}, (_, i) => i)
            }
            const pool_index = Math.floor(Math.random() * pool.length)
            // Taking the index out of the pool is what keeps it from repeating.
            const [random_index] = pool.splice(pool_index, 1)
            placeholder_pools[len] = pool
            const photo = random_index === undefined ? undefined : photos[random_index]
            present(photo, 'placeholder photo').primary = true
        } else {
            present(photos[0], 'product photo').primary = true
        }
    }

    Object.assign($s.product_photos, product_photos)
}

export function primary_photo(offer_item_artkey: number | undefined): string | undefined {
    if (!offer_item_artkey) {
        return
    }
    // An artkey whose photos were never asked for has no entry.
    const product_photos: Record<number, StateArray<ProductPhoto> | undefined> = $s.product_photos
    const photos = product_photos[offer_item_artkey]
    if (!photos || photos.length === 0) {
        return
    }

    const found_photo = photos.find((photo) => photo.primary)
    return found_photo?.thumbnail_location || found_photo?.image_location
}

export function unit_amount(caseAmount: number, caseBottles: number): number
export function unit_amount(caseAmount: number | null, caseBottles: number): number | null
export function unit_amount(caseAmount: number | null, caseBottles: number) {
    const amount = caseAmount
    if ($s.cart.show_bottles) {
        return Number(amount) * caseBottles
    }
    return amount
}

export function unit_price(casePrice: number | string | null, caseBottles: number) {
    const price = Number(casePrice)
    if ($s.cart.show_bottles) {
        return price / caseBottles
    }

    return price
}
